import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, IdentificationBadge, At, PencilSimple, Copy, ShieldCheck, Phone, Envelope, SealCheck, QrCode, CreditCard, CaretRight } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import OneKiteQR from "@/components/OneKiteQR";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Identity() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [handle, setHandle] = useState("");
  const [saving, setSaving] = useState(false);
  const [evOpen, setEvOpen] = useState(false);
  const [evCode, setEvCode] = useState("");
  const [evMock, setEvMock] = useState("");

  const requestEmail = async () => {
    try {
      await api.post("/customer/email/verify/request");
      setEvOpen(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const confirmEmail = async () => {
    try {
      await api.post("/customer/email/verify/confirm", { code: evCode });
      toast.success("Email verified"); setEvOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = () => api.get("/customer/identity").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const copyId = () => { navigator.clipboard?.writeText(d.onekite_id); toast.success("OneKite ID copied"); };

  const saveName = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/customer/onekite-name", { name: handle });
      toast.success(`OneKite Name set to @${data.onekite_name}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!d) return <div className="pt-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="pt-2 space-y-5">
      <button onClick={() => nav(-1)} data-testid="identity-back" className="text-muted-foreground flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="font-display font-bold text-2xl">Your OneKite identity</h1>
        <span data-testid="verification-badge" className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-1 flex items-center gap-1"><ShieldCheck size={13} weight="fill" /> {d.verification?.tier || "Basic"}</span>
      </div>

      {/* OneKite ID */}
      <div className="card-surface p-6">
        <div className="flex items-center gap-2 text-primary mb-3">
          <IdentificationBadge size={18} weight="duotone" />
          <span className="font-mono text-xs tracking-widest uppercase">OneKite ID</span>
        </div>
        <p className="font-display font-extrabold text-3xl tracking-wide tabular-nums" data-testid="identity-onekite-id">{d.onekite_id_formatted}</p>
        <div className="mt-4 flex items-center justify-end">
          <button data-testid="copy-onekite-id" onClick={copyId} className="flex items-center gap-1.5 text-sm text-primary font-medium shrink-0"><Copy size={16} /> Copy</button>
        </div>
      </div>

      {/* OneKite Name */}
      <div className="card-surface p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-muted-foreground">
            <At size={18} weight="bold" className="text-primary" />
            <span className="font-mono text-xs tracking-widest uppercase">OneKite Name</span>
          </div>
          <button data-testid="edit-onekite-name" onClick={() => { setHandle(d.onekite_name || ""); setOpen(true); }} className="flex items-center gap-1.5 text-sm text-primary font-medium"><PencilSimple size={16} /> {d.onekite_name ? "Change" : "Set"}</button>
        </div>
        <p className="font-display font-bold text-xl mt-2" data-testid="identity-onekite-name">{d.onekite_name ? `@${d.onekite_name}` : "Not set yet"}</p>
        <p className="text-xs text-muted-foreground mt-1">A unique, human-readable alias others can use to find you.</p>
      </div>

      {/* Verified aliases */}
      <div className="card-surface p-5">
        <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-3">Linked to your account</p>
        <div className="space-y-2.5">
          {d.aliases.map((a) => (
            <div key={a.type} className="flex items-center gap-3" data-testid={`alias-${a.type}`}>
              <span className="h-9 w-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                {a.type === "phone" ? <Phone size={16} weight="duotone" className="text-primary" /> : <Envelope size={16} weight="duotone" className="text-primary" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{a.value}</p>
                <p className="text-[11px] text-muted-foreground capitalize">{a.type}</p>
              </div>
              {a.verified ? (
                <span className="flex items-center gap-1 text-xs text-primary font-medium"><SealCheck size={14} weight="fill" /> Verified</span>
              ) : a.type === "email" ? (
                <button data-testid="verify-email" onClick={requestEmail} className="text-xs text-primary font-medium">Verify</button>
              ) : (
                <span className="text-xs text-muted-foreground">Unverified</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* QR */}
      <div className="card-surface p-6 flex flex-col items-center text-center">
        <div className="scale-90"><OneKiteQR value={d.qr_token} size={160} /></div>
        <Link data-testid="identity-open-qr" to="/app/qr" className="mt-4 flex items-center gap-2 text-sm text-primary font-medium"><QrCode size={16} /> Open full QR</Link>
        <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1"><ShieldCheck size={14} className="text-primary" /> Your QR carries a signed token. No balances or personal data.</p>
      </div>

      {/* Physical card */}
      <Link to="/app/link-card" data-testid="identity-link-card" className="card-surface p-5 flex items-center gap-3 hover:border-primary/40 transition-colors">
        <span className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center shrink-0"><CreditCard size={20} weight="duotone" className="text-primary" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Physical OneKite card</p>
          <p className="text-xs text-muted-foreground">Link an optional card. Same account, another way to present your ID.</p>
        </div>
        <CaretRight size={16} className="text-muted-foreground shrink-0" />
      </Link>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-black/10">
          <DialogHeader><DialogTitle className="font-display">Choose your OneKite Name</DialogTitle></DialogHeader>
          <Label className="text-xs text-muted-foreground">This is public and must be unique.</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-muted-foreground font-display font-bold text-lg">@</span>
            <Input data-testid="onekite-name-input" value={handle} onChange={(e) => setHandle(e.target.value.replace(/^@/, ""))} placeholder="stevenmatiku" className="bg-muted/40 border-black/10 h-11 rounded-xl" />
          </div>
          <p className="text-[11px] text-muted-foreground">3–20 characters · start with a letter · letters, numbers and underscore.</p>
          <Button data-testid="save-onekite-name" onClick={saveName} disabled={saving} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">{saving ? "Saving…" : "Save name"}</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={evOpen} onOpenChange={setEvOpen}>
        <DialogContent className="bg-card border-black/10">
          <DialogHeader><DialogTitle className="font-display">Verify your email</DialogTitle></DialogHeader>
          <div className="rounded-xl bg-primary/10 border border-primary/20 p-3 text-sm"><span className="text-muted-foreground">We emailed a 6-digit code to your address.</span></div>
          <Input data-testid="email-code-input" value={evCode} onChange={(e) => setEvCode(e.target.value)} placeholder="6-digit code" className="bg-muted/40 border-black/10 h-11 rounded-xl font-mono tracking-widest" />
          <Button data-testid="confirm-email" onClick={confirmEmail} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Confirm</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
